import { BrowserTools, ObjectTools, SolActiveRouteStatus } from '@sol/components';

export class AppState {
  private constructor(
    public readonly showNavbar: boolean,
    public readonly activeRouteStatus: SolActiveRouteStatus,
    public readonly contentClass: string,
    public readonly hostClass: string,
    /** True when running inside the Vue host shell (an iframe). Decided once at construction. */
    public readonly isEmbed: boolean,
    /**
     * Route-provided showNavbar flag from NavigationService.
     * Stored separately so embedded mode can suppress it without losing the route's
     * original intent for the standalone case.
     */
    public readonly routeShowNavbar: boolean,
  ) {}

  public static of(): AppState {
    const isEmbed = BrowserTools.isEmbedded();
    // In embedded mode the Angular navbar is always hidden — the Vue host shell
    // owns the chrome.
    const showNavbar = !isEmbed;
    return new AppState(showNavbar, 'loading', '', '', isEmbed, true).recalculate();
  }

  /**
   * Called when NavigationService reports a new route.
   * In embedded mode, showNavbar is always false regardless of the route data —
   * the Angular navbar must be hidden when running inside the Vue host shell.
   */
  public withNavigation(showNavbar: boolean, activeRouteStatus: SolActiveRouteStatus): AppState {
    // Compute effective showNavbar: embedded mode always hides the navbar.
    const effectiveShowNavbar = this.isEmbed ? false : showNavbar;
    if (
      this.showNavbar === effectiveShowNavbar &&
      this.routeShowNavbar === showNavbar &&
      this.activeRouteStatus === activeRouteStatus
    ) {
      return this;
    }
    return this.copy({
      showNavbar: effectiveShowNavbar,
      routeShowNavbar: showNavbar,
      activeRouteStatus,
    }).recalculate();
  }

  private recalculate(): AppState {
    const contentClass = 'sol-app-content';
    const hostClass = this.showNavbar || this.isEmbed ? 'sol-app-shell' : '';
    return this.copy({ contentClass, hostClass });
  }

  private copy(props: Partial<AppState>): AppState {
    return ObjectTools.copy<AppState>(this, props);
  }
}
